import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { FormCheckbox, FormField, FormSelect } from '../../components/FormField'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { RecordLink } from '../../components/RecordLink'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TablePanelRow, TableScroll } from '../../components/TableScroll'
import { historyRow } from '../../components/historyRow'
import type { ErrorSubject } from '../../lib/api'
import { apiErrorMessage } from '../../lib/errors'
import { formatDate, formatMoney, formatNumber } from '../../lib/format'
import { keepDecimal, parseAmount } from '../../lib/number'
import { forgetDraft, keepDraft, keptDraft } from '../../lib/sessionDrafts'
import { isUnreadableFigure, NO_VIEW, type TableColumn, type TableViewState } from '../../lib/tableView'
import { useTableView } from '../../lib/useTableView'
import { useAuth } from '../auth/context'
import {
  EQUIPMENT_TYPES,
  featuresAllowedOn,
  isOpenDeck,
  type EquipmentFeature,
  type EquipmentType,
} from '../carriers/api'
import { LOAD_TYPES, SECUREMENT_GEAR, type LoadType, type SecurementGear } from '../availability/api'
import {
  captureDatRows,
  readDatPaste,
  resolveDatBrokers,
  type DatBrokerQuery,
  type DatBrokerResolution,
  type DatCaptureOutcome,
  type DatCaptureRow,
  type DatPasteNote,
  type DatPasteReading,
  type DatPasteRow,
} from './api'
import { DatBrokersPanel } from './DatBrokersPanel'
import { loadMessageFor } from './errors'
import { companiesOf, companyKey, contactsOf, questionOf, type ChosenBroker } from './pasteCompanies'

/**
 * A row as the dispatcher is reviewing it: what DAT's reading said, plus what only they can
 * add — the notes they keep for the load, and a reefer's temperatures, which DAT never prints
 * (TRKC-110). Theirs, never DAT's, so the reading itself never carries them.
 */
type ReviewedRow = DatPasteRow & {
  notes: string | null
  reeferMinFahrenheit: number | null
  reeferMaxFahrenheit: number | null
}

/** The note that names a row's skipped values — it follows the row it is about. */
const SKIPPED_IN_A_ROW = 'paste_values_skipped_in_a_row'

/**
 * Notes that report nothing to act on — a detail panel the reader recognised and set aside —
 * said as plain information beside the count, never inside the warning. Under the heading
 * "some values were left out" a recognised panel still read as the anomaly it is not
 * (GPT-5.6 Sol on #39).
 */
const INFORMATION: ReadonlySet<string> = new Set(['paste_detail_panel_open'])

/** What the load's own notes hold at most; the capture refuses longer ones. */
const MAX_NOTES_LENGTH = 1000

/** What a reefer holds, as the server's ReeferRange bounds it — mirrored for the row editor. */
const LOWEST_FAHRENHEIT = -40
const HIGHEST_FAHRENHEIT = 90

/**
 * The paste under review, kept for this browser tab (TRKC-110, item 10): looking at a broker
 * or a load and coming back must not throw away a reading the dispatcher has corrected and
 * ticked. The book's answers are not kept — they are asked again on the way back, about a
 * book that may have changed meanwhile.
 */
const DRAFT = 'dat-paste'

interface PasteDraft {
  text: string
  /** Null while the text has not been read — pasted, then a look elsewhere before Read. */
  reading: DatPasteReading | null
  rows: ReviewedRow[]
  chosen: number[]
  outcomes: Record<number, DatCaptureOutcome>
  choices: Record<string, Answered<ChosenBroker | null>>
  lastCapture: { captured: number; refused: number } | null
  view: TableViewState
}

/** A row's route as it reads: what the route column sorts and filters by. */
function routeText(row: DatPasteRow): string {
  const place = (city: string | null, state: string | null) => [city, state].filter((part) => part !== null).join(', ')
  return `${place(row.originCity, row.originState)} → ${place(row.destinationCity, row.destinationState)}`
}

/**
 * What each column of the paste sorts and filters by (TRKC-110). The rate and the rate per mile
 * keep a load with no rate posted under a "least" filter — its rate is negotiated, not low —
 * while a broker DAT has not scored is not a broker with a good score (items 3 and 9).
 */
const PASTE_COLUMNS: readonly TableColumn<ReviewedRow>[] = [
  { key: 'route', sortValue: routeText, filter: { kind: 'text', text: routeText } },
  { key: 'pickUp', sortValue: (row) => row.pickUpFrom, filter: { kind: 'onOrAfter', value: (row) => row.pickUpFrom } },
  {
    key: 'freight',
    sortValue: (row) => row.equipmentCode,
    filter: {
      kind: 'choice',
      values: EQUIPMENT_TYPES,
      matches: (row, type) => row.equipment === type || row.alternativeEquipment === type,
    },
  },
  { key: 'rate', sortValue: (row) => row.rate, filter: { kind: 'atLeast', value: (row) => row.rate, keepUnknown: true } },
  {
    key: 'perMile',
    sortValue: (row) => row.ratePerMile,
    filter: { kind: 'atLeast', value: (row) => row.ratePerMile, keepUnknown: true },
  },
  {
    key: 'credit',
    sortValue: (row) => row.creditScore,
    filter: { kind: 'atLeast', value: (row) => row.creditScore, keepUnknown: false },
  },
  {
    key: 'daysToPay',
    sortValue: (row) => row.daysToPay,
    filter: { kind: 'atMost', value: (row) => row.daysToPay, keepUnknown: false },
  },
  {
    key: 'broker',
    sortValue: (row) => row.company,
    filter: { kind: 'text', text: (row) => [row.company ?? '', ...contactsOf(row).map(contactText)].join(' ') },
  },
]

/** The columns whose "least" filter keeps a load with no rate posted, which the screen says. */
const RATE_COLUMNS = ['rate', 'perMile']

/**
 * A DAT search, pasted, read back, and captured.
 *
 * <p>
 * The dispatcher copies their results out of the board, drops them here, and sees what
 * the platform understood — row by row, with what it could not read said out loud. They
 * tick the ones they want and correct whatever DAT printed oddly; the book says which
 * broker each company is when it knows, and a company it does not know is filed under the
 * name DAT printed — Hugo registers a broker when he books one of its loads, never to
 * capture one (TRKC-104). Only the capture writes anything, and each row answers for
 * itself: its load, or why not.
 * </p>
 */
export function DatPastePage() {
  const { t, i18n } = useTranslation()
  const userId = useAuth().user?.id ?? null

  const [text, setText] = useState('')
  const [reading, setReading] = useState<DatPasteReading | null>(null)
  const [rows, setRows] = useState<ReviewedRow[]>([])
  const [chosen, setChosen] = useState<ReadonlySet<number>>(new Set())
  const [editing, setEditing] = useState<number | null>(null)
  const [isReading, setIsReading] = useState(false)
  // Kept unresolved and translated at render, so a language switch re-translates it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null)

  // The book's answer about each company, with the question it answered. A broker it did not
  // offer is searched for in the company's picker.
  const [resolutions, setResolutions] = useState<Record<string, BookAnswer>>({})
  // Every time the book is asked counts as a round; the capture rests only on the latest one.
  const askRound = useRef(0)
  const [latestRound, setLatestRound] = useState(0)
  const [isChecking, setIsChecking] = useState(false)
  const [checkFailure, setCheckFailure] = useState<{ error: unknown } | null>(null)
  // Bumped to ask the book again: "Check again", and coming back to this tab — where a
  // dispatcher lands after registering a broker in the other one.
  const [askAgain, setAskAgain] = useState(0)
  // What the dispatcher chose per company, on the question it was chosen on; null is
  // "none", a choice too.
  const [choices, setChoices] = useState<Record<string, Answered<ChosenBroker | null>>>({})

  const [outcomes, setOutcomes] = useState<Record<number, DatCaptureOutcome>>({})
  const [isCapturing, setIsCapturing] = useState(false)
  const [captureFailure, setCaptureFailure] = useState<{ error: unknown } | null>(null)
  const [lastCapture, setLastCapture] = useState<{ captured: number; refused: number } | null>(null)
  // Moved by every reading and by starting over: an answer about an earlier paste must
  // land on nothing, never on the rows that replaced it.
  const generation = useRef(0)

  const capturedLoad = (number: number): string | null => outcomes[number]?.loadId ?? null

  // The rows as the dispatcher sorted and narrowed them (items 3, 5 and 9). Only the rows on
  // screen are created: a ticked row a filter hides stays ticked, says so, and waits.
  const view = useTableView(rows, PASTE_COLUMNS)
  const { replace: replaceView } = view
  const shown = new Set(view.rows.map((row) => row.number))

  // Whose work the page holds: the user it was put back for, or who was signed in when it was
  // begun. Another tab signing in as somebody else hands this tab that session on its next
  // refresh, with the page still mounted: the first user's paste must then go — never kept,
  // or sent, as the second's (GPT-6.1 Sol on #58).
  const [owner, setOwner] = useState<string | null>(null)

  useEffect(() => {
    if (userId === null || owner === userId) {
      return
    }

    if (owner !== null) {
      // Every answer still on its way is about the other user's paste. Nothing of the new
      // user's is kept here to put back: while the page was the other's, the tab's one draft
      // was theirs, or none.
      generation.current += 1
      setText('')
      setReading(null)
      setRows([])
      setChosen(new Set())
      setEditing(null)
      setFailure(null)
      setResolutions({})
      setCheckFailure(null)
      setChoices({})
      setOutcomes({})
      setCaptureFailure(null)
      setLastCapture(null)
      replaceView(NO_VIEW)
      setOwner(userId)
      return
    }
    setOwner(userId)

    // The paste this user kept for the tab, put back over an empty page only: one already on
    // screen is never replaced.
    const kept = keptDraft<PasteDraft>(DRAFT, userId)
    if (kept === null || text !== '' || reading !== null) {
      return
    }

    setText(kept.text)
    setReading(kept.reading)
    // A paste kept before the temperatures existed has none: the row says so, and waits.
    const restored = kept.rows.map((row) => ({
      ...row,
      reeferMinFahrenheit: row.reeferMinFahrenheit ?? null,
      reeferMaxFahrenheit: row.reeferMaxFahrenheit ?? null,
    }))
    setRows(restored)
    // ...and loses its tick: Create sends what is ticked, so a row no longer ready — a reefer
    // ticked before its temperatures were asked — would reach a capture that refuses it
    // (Copilot on #60). The dispatcher ticks it again once it is corrected.
    const ready = new Set(restored.filter(isReady).map((row) => row.number))
    setChosen(new Set(kept.chosen.filter((number) => ready.has(number))))
    setOutcomes(kept.outcomes)
    setChoices(kept.choices)
    setLastCapture(kept.lastCapture)
    replaceView(kept.view)
    // Runs on every change of the page, and acts once per user: the guard above returns.
  }, [userId, owner, text, reading, replaceView])

  // Kept on every change — the text alone too, pasted and not read yet (Copilot on #58) — and
  // forgotten when there is nothing to keep (Start over). Only once the page is the signed-in
  // user's: until then it holds nothing of theirs to keep, or a draft of theirs still to read.
  useEffect(() => {
    if (userId === null || owner !== userId) {
      return
    }

    if (text === '' && reading === null) {
      forgetDraft(DRAFT)
      return
    }

    keepDraft<PasteDraft>(DRAFT, userId, {
      text,
      reading,
      rows,
      chosen: [...chosen],
      outcomes,
      choices,
      lastCapture,
      view: view.state,
    })
  }, [userId, owner, text, reading, rows, chosen, outcomes, choices, lastCapture, view.state])

  // The companies the book is asked about: every row that could still become a load.
  const companies = useMemo(
    () => companiesOf(rows.filter((row) => !row.isCancelled && (outcomes[row.number]?.loadId ?? null) === null)),
    [rows, outcomes],
  )

  // Keyed on what is asked, not on the array: a correction that leaves every company and
  // contact as it was asks nothing new.
  const question = JSON.stringify(companies.map(({ key, company, contacts }) => ({ key, company, contacts })))

  useEffect(() => {
    const asked = JSON.parse(question) as DatBrokerQuery[]

    if (asked.length === 0) {
      return
    }

    let active = true
    const round = (askRound.current += 1)
    setLatestRound(round)
    setIsChecking(true)
    setCheckFailure(null)

    // The last question asked is the one answered: an earlier answer landing late would
    // name brokers for companies the rows no longer hold.
    void resolveDatBrokers(asked)
      .then((answer) => {
        if (active) {
          const askedAbout = new Map(asked.map((query) => [query.key, questionOf(query)]))
          setResolutions(
            Object.fromEntries(
              answer.companies.map((company) => [
                company.key,
                { asked: askedAbout.get(company.key) ?? '', value: company, round },
              ]),
            ),
          )
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setCheckFailure({ error: caught })
        }
      })
      .finally(() => {
        if (active) {
          setIsChecking(false)
        }
      })

    return () => {
      active = false
    }
  }, [question, askAgain])

  useEffect(() => {
    const again = () => setAskAgain((count) => count + 1)
    window.addEventListener('focus', again)
    return () => window.removeEventListener('focus', again)
  }, [])

  /**
   * The book's answers that still answer the company as it stands. One asked about contacts
   * a correction has since changed is about somebody else: while the new answer is on its
   * way — or if it never comes — the company waits, and never files under the old broker
   * (Copilot on #40).
   */
  const currentResolutions: Record<string, DatBrokerResolution> = Object.fromEntries(
    companies.flatMap((company) => {
      const answered = resolutions[company.key]
      return answered !== undefined && answered.asked === questionOf(company) ? [[company.key, answered.value]] : []
    }),
  )

  /** The broker a company's loads are filed under: the dispatcher's choice, or the one the book knows. */
  function brokerOf(key: string): ChosenBroker | null {
    const company = companies.find((candidate) => candidate.key === key)

    if (company === undefined) {
      return null
    }

    // A choice is made on what the rows said then, and is not carried onto contacts changed since.
    const choice = choices[key]
    if (choice !== undefined && choice.asked === questionOf(company)) {
      return choice.value
    }

    const answer = currentResolutions[key]
    const known = answer?.status === 'Known' ? answer.brokers[0] : undefined
    return known === undefined ? null : { id: known.brokerId, name: known.name }
  }

  function chooseBroker(key: string, broker: ChosenBroker | null) {
    const company = companies.find((candidate) => candidate.key === key)

    if (company === undefined) {
      return
    }

    setChoices((current) => ({ ...current, [key]: { asked: questionOf(company), value: broker } }))

    // A refusal about the broker these rows had is not about the one they have now.
    setOutcomes((current) => withoutRefusals(current, company.rows))
  }

  async function handleRead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    generation.current += 1
    const asOf = generation.current
    setIsReading(true)
    setFailure(null)
    setEditing(null)
    forgetCapture()

    try {
      const answer = await readDatPaste(text)

      if (asOf !== generation.current) {
        return
      }

      setReading(answer)
      setRows(answer.rows.map((row) => ({ ...row, notes: null, reeferMinFahrenheit: null, reeferMaxFahrenheit: null })))
      // A row DAT has cancelled, or one short of a load, starts unticked: the dispatcher
      // opts those in deliberately, never by missing them.
      setChosen(new Set(answer.rows.filter((row) => row.isComplete && !row.isCancelled).map((row) => row.number)))
    } catch (caught) {
      if (asOf === generation.current) {
        setReading(null)
        setRows([])
        setFailure({ error: caught })
      }
    } finally {
      setIsReading(false)
    }
  }

  function clear() {
    generation.current += 1
    setText('')
    setReading(null)
    setRows([])
    setChosen(new Set())
    setEditing(null)
    setFailure(null)
    view.replace(NO_VIEW)
    forgetCapture()
  }

  /** Everything the capture knew about the previous paste. */
  function forgetCapture() {
    setResolutions({})
    setCheckFailure(null)
    setChoices({})
    setOutcomes({})
    setCaptureFailure(null)
    setLastCapture(null)
  }

  function choose(number: number, wanted: boolean) {
    setChosen((current) => {
      const next = new Set(current)
      if (wanted) {
        next.add(number)
      } else {
        next.delete(number)
      }
      return next
    })
  }

  function applyEdit(edited: ReviewedRow) {
    // What the reader skipped stays on the row until the dispatcher decides about each
    // value in the editor — into the notes, or left out. Applying is not that decision: it
    // used to clear them all, so a row became ready without anyone looking, and something
    // like "Tarps Required" vanished from the load (GPT-5.6 Sol on #37).
    setRows((current) => current.map((row) => (row.number === edited.number ? edited : row)))
    setEditing(null)

    // A refusal was about the values the row had; corrected, it has earned another try.
    setOutcomes((current) => withoutRefusals(current, [edited.number]))

    // A row corrected into a load can be taken; one corrected out of being one cannot.
    if (!isReady(edited)) {
      choose(edited.number, false)
    }
  }

  async function capture() {
    const asOf = generation.current
    const taking = rows.filter((row) => chosen.has(row.number) && shown.has(row.number))

    setIsCapturing(true)
    setCaptureFailure(null)
    setLastCapture(null)

    try {
      const answer = await captureDatRows(
        taking.map((row) => captureRowOf(row, brokerOf(companyKey(row.company ?? ''))?.id ?? null)),
      )

      if (asOf !== generation.current) {
        return
      }

      setOutcomes((current) => ({
        ...current,
        ...Object.fromEntries(answer.rows.map((outcome) => [outcome.number, outcome])),
      }))

      // A captured row is done: it leaves the selection and cannot be taken again. A
      // refused one stays ticked, with its reasons under it.
      const filed = new Set(answer.rows.filter((outcome) => outcome.loadId !== null).map((outcome) => outcome.number))
      setChosen((current) => new Set([...current].filter((number) => !filed.has(number))))
      setLastCapture({ captured: answer.captured, refused: answer.refused })
    } catch (caught) {
      if (asOf === generation.current) {
        setCaptureFailure({ error: caught })
      }
    } finally {
      setIsCapturing(false)
    }
  }

  // Counted the way the server counts it: a row we would tick for the dispatcher. A
  // cancelled one is complete but never offered, and one already captured is done.
  const ready = rows.filter((row) => isReady(row) && !row.isCancelled && capturedLoad(row.number) === null).length
  const taking = rows.filter((row) => chosen.has(row.number) && shown.has(row.number))
  const taken = taking.length
  const hiddenTaken = rows.filter((row) => chosen.has(row.number) && !shown.has(row.number)).length
  // The companies of the rows that will be created, and no other: a hidden row's company,
  // its book's answer still on its way, held Create back for a load it would not send
  // (GPT-6.1 Sol on #58).
  const takenNumbers = new Set(taking.map((row) => row.number))
  const selectedCompanies = companies.filter((company) => company.rows.some((number) => takenNumbers.has(number)))

  // A company is filed under its broker when the book knows it, and under its own name when
  // not — never waiting for anybody to register it (TRKC-104). What the capture does wait
  // for is the book's answer about the company as it stands: a load filed under the
  // company's name because the button outran the book would sit beside the broker's own copy
  // of it on the next paste. A choice made on the company as it stands needs no answer. An
  // answer from before the book was asked again — on coming back to the tab, or *Check
  // again* — may be about a book that changed since (a broker registered in the other tab),
  // so the capture waits for the new one; the panel keeps showing the older answer
  // meanwhile (GPT-6.1 Sol on #50).
  const unanswered = selectedCompanies.filter(
    (company) =>
      choices[company.key]?.asked !== questionOf(company) &&
      (currentResolutions[company.key] === undefined || resolutions[company.key]?.round !== latestRound),
  )
  // Never a paste the signed-in user did not make: a tab changing hands holds the other
  // user's rows for one render, before they are let go — and a click arriving then is handled
  // by that render. No test can stand in that window; this guard is the only thing in it.
  const canCapture = taken > 0 && unanswered.length === 0 && editing === null && !isCapturing && owner === userId

  // Why the button is off, said beside it — where the eye goes when it does not answer.
  // Hugo met 27 loads waiting and a grey line at the other side of the screen (TRKC-104).
  const reasons = isCapturing
    ? []
    : [
        ...(taken === 0 ? [t('loads.paste.nothingTicked')] : []),
        ...(taken > 0 && unanswered.length > 0
          ? [checkFailure === null ? t('loads.paste.waitingForBook') : t('loads.paste.bookUnread')]
          : []),
        ...(editing !== null && taken > 0 ? [t('loads.paste.finishEditing')] : []),
      ]

  // Every row on screen that can still be taken: complete, not cancelled by DAT, not created
  // yet. Ticking thirty rows off one by one is not practical (Hugo, TRKC-104). A row the
  // filters hide is left as it was.
  const takeable = view.rows.filter((row) => isReady(row) && !row.isCancelled && capturedLoad(row.number) === null)
  const takenOfTakeable = takeable.filter((row) => chosen.has(row.number)).length
  const allTaken = takeable.length > 0 && takenOfTakeable === takeable.length

  function takeAll(wanted: boolean) {
    setChosen((current) => {
      const next = new Set(current)
      takeable.forEach((row) => (wanted ? next.add(row.number) : next.delete(row.number)))
      return next
    })
  }

  // A "least" filter on the rate keeps the loads with no rate posted, and the screen says why.
  const rateFiltered = RATE_COLUMNS.some((column) => view.filterOf(column).trim() !== '' && !isUnreadableFigure(view.filterOf(column)))
  const notes = reading === null ? [] : reading.notes.flatMap((note) => stillPending(note, rows))
  const warnings = notes.filter((note) => !INFORMATION.has(note.code))
  const information = notes.filter((note) => INFORMATION.has(note.code))

  return (
    <section className="page page--wide">
      <Breadcrumbs trail={[{ label: t('nav.loads'), to: '/loads' }, { label: t('loads.book.paste') }]} />
      <header className="page__header">
        <h1>{t('loads.paste.title')}</h1>
        <p className="page__subtitle">{t('loads.paste.subtitle')}</p>
      </header>

      <form className="form" onSubmit={handleRead} noValidate>
        <div className="loads__box">
          <FormField
            name="paste"
            label={t('loads.paste.label')}
            value={text}
            onChange={setText}
            placeholder={t('loads.paste.placeholder')}
            multiline
            rows={8}
          />
          <p className="field__hint">{t('loads.paste.how')}</p>
        </div>

        <div className="form__break" />

        <div className="form__actions">
          {/* Neither waits on a capture in flight: its answer is about these rows. */}
          <button className="button" type="submit" disabled={isReading || isCapturing || text.trim() === ''}>
            {isReading ? t('loads.paste.reading') : t('loads.paste.read')}
          </button>
          {reading !== null && (
            <button className="button button--quiet" type="button" onClick={clear} disabled={isCapturing}>
              {t('loads.paste.clear')}
            </button>
          )}
        </div>
      </form>

      {failure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(failure.error, t, 'loads.paste.failed')}
        </p>
      )}

      {/* Said whatever came back: a paste that was refused for its size, or that held
          no rows at all, is exactly when the server's explanation is the only thing
          worth reading (Copilot on #29 — it used to be hidden with the table). */}
      {warnings.length > 0 && (
        <div className="alert alert--warning">
          <p>{t('loads.paste.notesHeading')}</p>
          <ul>
            {warnings.map((note) => (
              <li key={`${note.code}-${JSON.stringify(note.values)}`}>{noteSentence(note, t)}</li>
            ))}
          </ul>
        </div>
      )}

      {reading !== null && reading.rows.length === 0 && (
        <p className="placeholder">{t('loads.paste.nothing')}</p>
      )}

      {reading !== null && reading.rows.length > 0 && (
        <>
          <p className="placeholder" role="status">
            {/* One phrase per count: Spanish agrees with each of them, and a single
                sentence with several numbers in it cannot. The repeats merged are said
                only when there are any. */}
            {[
              t('loads.paste.summaryRead', { count: reading.summary.read }),
              reading.summary.merged > 0 ? t('loads.paste.summaryMerged', { count: reading.summary.merged }) : null,
              t('loads.paste.summaryReady', { count: ready }),
              t('loads.paste.summaryCancelled', { count: reading.summary.cancelled }),
            ]
              .filter((part) => part !== null)
              .join(' · ')}
          </p>

          {information.map((note) => (
            <p key={`${note.code}-${JSON.stringify(note.values)}`} className="field__hint">
              {noteSentence(note, t)}
            </p>
          ))}

          <TableScroll label={t('loads.paste.title')} className="table-scroll--panels">
            <table className="table">
              <caption className="visually-hidden">{t('loads.paste.title')}</caption>
              <thead>
                <tr>
                  <th scope="col">
                    {/* All or none: checked when every row that can be taken is, mixed when
                        some are. Unticking leaves nothing selected. */}
                    <input
                      type="checkbox"
                      checked={allTaken}
                      ref={(box) => {
                        if (box !== null) {
                          box.indeterminate = takenOfTakeable > 0 && !allTaken
                        }
                      }}
                      disabled={isCapturing || takeable.length === 0}
                      aria-label={t('loads.paste.takeAll')}
                      onChange={(event) => takeAll(event.target.checked)}
                    />
                  </th>
                  <SortableHeader view={view} column="route">{t('loads.paste.route')}</SortableHeader>
                  <SortableHeader view={view} column="pickUp">{t('loads.paste.pickUp')}</SortableHeader>
                  <SortableHeader view={view} column="freight">{t('loads.paste.freight')}</SortableHeader>
                  <SortableHeader view={view} column="rate">{t('loads.paste.rate')}</SortableHeader>
                  <SortableHeader view={view} column="perMile">{t('loads.paste.perMileHeading')}</SortableHeader>
                  <SortableHeader view={view} column="credit">{t('loads.paste.creditHeading')}</SortableHeader>
                  <SortableHeader view={view} column="daysToPay">{t('loads.paste.daysToPayHeading')}</SortableHeader>
                  <SortableHeader view={view} column="broker">{t('loads.paste.broker')}</SortableHeader>
                  <th scope="col">
                    <span className="visually-hidden">{t('loads.paste.edit')}</span>
                  </th>
                </tr>
                <tr className="table__filters">
                  <td />
                  {PASTE_COLUMNS.map((column) => (
                    <FilterCell
                      key={column.key}
                      view={view}
                      column={column}
                      label={t(PASTE_HEADINGS[column.key] ?? column.key)}
                      // A filter could hide the row being corrected, and its editor with it —
                      // the typing lost and Create held for an Apply nobody can see (Copilot on #58).
                      disabled={editing !== null}
                      options={
                        column.key === 'freight'
                          ? EQUIPMENT_TYPES.map((type) => ({ value: type, label: t(`carriers.types.${type}`) }))
                          : undefined
                      }
                    />
                  ))}
                  <td />
                </tr>
              </thead>
              <tbody>
                {view.rows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="placeholder">
                      {t('loads.book.noneMatch')}
                    </td>
                  </tr>
                )}
                {view.rows.map((row) => (
                  <RowLines
                    key={row.number}
                    row={row}
                    language={i18n.language}
                    chosen={chosen.has(row.number)}
                    onChoose={(wanted) => choose(row.number, wanted)}
                    isEditing={editing === row.number}
                    onEdit={() => setEditing(row.number)}
                    onCancel={() => setEditing(null)}
                    onSave={applyEdit}
                    outcome={outcomes[row.number]}
                    disabled={isCapturing}
                  />
                ))}
              </tbody>
            </table>
          </TableScroll>

          <FilterSummary view={view} />
          {rateFiltered && <p className="field__hint">{t('loads.paste.rateFilterHint')}</p>}

          <p className="field__hint">{t('loads.paste.taken', { count: taken })}</p>
          {hiddenTaken > 0 && <p className="field__hint">{t('loads.paste.hiddenTaken', { count: hiddenTaken })}</p>}

          {selectedCompanies.length > 0 && (
            <DatBrokersPanel
              companies={selectedCompanies}
              resolutions={currentResolutions}
              brokerOf={brokerOf}
              onChoose={chooseBroker}
              isChecking={isChecking}
              failure={checkFailure}
              onCheckAgain={() => setAskAgain((count) => count + 1)}
              disabled={isCapturing}
            />
          )}

          <div className="form__actions loads__capture">
            {reasons.length > 0 && (
              <div className="alert alert--warning loads__capture-why">
                <div id="paste-capture-why">
                  {reasons.map((reason) => (
                    <p key={reason}>{reason}</p>
                  ))}
                </div>
              </div>
            )}
            <button
              className="button"
              type="button"
              onClick={() => void capture()}
              disabled={!canCapture}
              aria-describedby={reasons.length > 0 ? 'paste-capture-why' : undefined}
            >
              {isCapturing ? t('loads.paste.capturing') : t('loads.paste.capture', { count: taken })}
            </button>
          </div>

          {captureFailure !== null && (
            <p className="alert" role="alert">
              {apiErrorMessage(captureFailure.error, t, 'loads.paste.captureFailed')}
            </p>
          )}

          {/* Mounted before anything is captured, so the result is announced when it lands. */}
          <div aria-live="polite">
            {lastCapture !== null && (
              <p className="placeholder">
                {[
                  t('loads.paste.capturedCount', { count: lastCapture.captured }),
                  lastCapture.refused > 0 ? t('loads.paste.refusedCount', { count: lastCapture.refused }) : null,
                ]
                  .filter((part) => part !== null)
                  .join(' · ')}
                {lastCapture.captured > 0 && (
                  <span>
                    {' · '}
                    <Link to="/loads">{t('loads.paste.openBook')}</Link>
                  </span>
                )}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  )
}

/** The heading each column's filter box is named after. */
const PASTE_HEADINGS: Record<string, string> = {
  route: 'loads.paste.route',
  pickUp: 'loads.paste.pickUp',
  freight: 'loads.paste.freight',
  rate: 'loads.paste.rate',
  perMile: 'loads.paste.perMileHeading',
  credit: 'loads.paste.creditHeading',
  daysToPay: 'loads.paste.daysToPayHeading',
  broker: 'loads.paste.broker',
}

/** The tick box, the columns, and the Correct button: what a row under a row spans. */
const COLUMN_COUNT = PASTE_COLUMNS.length + 2

/** A rate over a trip, to the cent; none when either is missing. */
function perMile(rate: number | null, tripMiles: number | null): number | null {
  return rate === null || tripMiles === null || tripMiles <= 0 ? null : Math.round((rate / tripMiles) * 100) / 100
}

/**
 * A note's own sentence, in the portal's language. A code this build does not know gets
 * a translated generic — never the server's English, which is the whole reason the API
 * sends a code (the error contract, applied to notes).
 */
function noteSentence(note: DatPasteNote, t: TFunction): string {
  return t(`loads.paste.note.${note.code}`, {
    ...note.values,
    defaultValue: t('loads.paste.note.unknown'),
  })
}

/**
 * A note as it stands now. The one naming a row's skipped values follows that row: it
 * names what is still waiting and goes once nothing is — so the screen never reads
 * "ready" under a warning about the same row (GPT-5.6 Sol on #37).
 */
function stillPending(note: DatPasteNote, rows: ReviewedRow[]): DatPasteNote[] {
  const row = note.code === SKIPPED_IN_A_ROW ? rows.find((candidate) => String(candidate.number) === note.values.row) : undefined

  if (row === undefined) {
    return [note]
  }

  return row.skipped.length === 0 ? [] : [{ ...note, values: { ...note.values, sample: sample(row.skipped) } }]
}

/** The server's sample of a list of values — the first three, quoted and shortened — mirrored. */
function sample(values: string[]): string {
  const quoted = values.slice(0, 3).map((value) => `"${value.length <= 40 ? value : `${value.slice(0, 40)}…`}"`)
  return quoted.join(', ') + (values.length > 3 ? ', …' : '')
}

/**
 * A refusal in the dispatcher's words. A stop's complaint says which stop — a row has two,
 * the pickup at its origin and the delivery at its destination — because the load form's
 * sentence ("Enter the city.") assumes the box is right beside it.
 */
function refusalSentence(code: string, t: TFunction, subject: ErrorSubject | undefined): string {
  const stop = /^load_stop_(\d+)_/.exec(code)
  const sentence = loadMessageFor(code, t, subject)

  return stop === null
    ? sentence
    : t('loads.paste.atStop', { stop: t(`loads.form.stopTypes.${stop[1] === '1' ? 'Pickup' : 'Delivery'}`), sentence })
}

/** A contact as the row shows it: the address, the phone, or both. */
function contactText(contact: { email: string | null; phone: string | null }): string {
  return [contact.email, contact.phone].filter((part) => part !== null).join(' / ')
}

/** Something said about a company, and the question it was said on (`questionOf`). */
interface Answered<T> {
  asked: string
  value: T
}

/** The book's answer about a company, and the round of asking it came from. */
type BookAnswer = Answered<DatBrokerResolution> & { round: number }

/** The outcomes without the refusals of these rows; a row already captured keeps its load. */
function withoutRefusals(
  outcomes: Record<number, DatCaptureOutcome>,
  numbers: number[],
): Record<number, DatCaptureOutcome> {
  const stale = new Set(numbers.filter((number) => outcomes[number] !== undefined && outcomes[number].loadId === null))

  return stale.size === 0
    ? outcomes
    : Object.fromEntries(Object.entries(outcomes).filter(([number]) => !stale.has(Number(number))))
}

/**
 * A row as the capture takes it: what the dispatcher approved, under the broker the book
 * knows or they chose — or, with none, under the company's name as the row stands.
 */
function captureRowOf(row: ReviewedRow, brokerId: string | null): DatCaptureRow {
  return {
    number: row.number,
    isCancelled: row.isCancelled,
    brokerId,
    company: row.company,
    originCity: row.originCity,
    originState: row.originState,
    destinationCity: row.destinationCity,
    destinationState: row.destinationState,
    pickUpFrom: row.pickUpFrom,
    pickUpTo: row.pickUpTo,
    equipment: row.equipment,
    // "A reefer, or a reefer" is a reefer: a base corrected onto the alternative leaves none.
    alternativeEquipment: row.alternativeEquipment === row.equipment ? null : row.alternativeEquipment,
    equipmentCode: row.equipmentCode,
    loadType: row.loadType,
    weightPounds: row.weightPounds,
    lengthFeet: row.lengthFeet,
    tripMiles: row.tripMiles,
    rate: row.rate,
    // Every contact DAT listed for the load — its own row's and the repeats' — in DAT's order.
    contacts: contactsOf(row),
    notes: row.notes,
    // What the code asks of the unit, as read and corrected (TRKC-100).
    features: row.features,
    securement: row.securement,
    hazmat: row.hazmat,
    reeferMinFahrenheit: row.reeferMinFahrenheit,
    reeferMaxFahrenheit: row.reeferMaxFahrenheit,
  }
}

/**
 * What a row needs before it can be a load — the same facts the server's own reading
 * checks. Mirrored here (and only here) so a row the dispatcher has just corrected can be
 * ticked without a round trip; the capture re-judges it anyway.
 */
function isReady(row: ReviewedRow): boolean {
  return (
    // Both halves of each place: the reader only ever produces "City, ST" or nothing,
    // but the editor holds them in two boxes and can empty one (Copilot on #29).
    row.originCity !== null &&
    row.originState !== null &&
    row.destinationCity !== null &&
    row.destinationState !== null &&
    row.pickUpFrom !== null &&
    // What a load refuses to be without: a type it rides on, its weight, full or partial.
    // A code like "SD" names no type the platform carries (GPT-5.6 Sol on #37).
    row.equipment !== null &&
    row.weightPounds !== null &&
    row.loadType !== null &&
    // A load that rides in a reefer states its temperatures, and DAT prints none: the
    // dispatcher types them (TRKC-110). One that only also takes a reefer (VR) need not.
    (row.equipment !== 'Reefer' || (row.reeferMinFahrenheit !== null && row.reeferMaxFahrenheit !== null)) &&
    row.company !== null &&
    // A way to reach the broker: the row's own contact or, on a load posted by several
    // reps, any of the others. The reader always gives a row its own, but the editor can
    // empty it — a wrong rep — while the others still reach the broker (GPT-5.6 Sol on #40).
    contactsOf(row).length > 0 &&
    // Something the reader could not place, in the middle of the row: it waits for a
    // person, as the server's reading does (TRKC-93).
    row.skipped.length === 0
  )
}

function RowLines({
  row,
  language,
  chosen,
  onChoose,
  isEditing,
  onEdit,
  onCancel,
  onSave,
  outcome,
  disabled,
}: {
  row: ReviewedRow
  language: string
  chosen: boolean
  onChoose: (wanted: boolean) => void
  isEditing: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: (row: ReviewedRow) => void
  /** What the capture answered for this row, once it has. */
  outcome: DatCaptureOutcome | undefined
  /** A capture is in flight: nothing on the row changes under it. */
  disabled: boolean
}) {
  const { t } = useTranslation()
  const place = (city: string | null, state: string | null) =>
    city === null ? t('loads.paste.missing') : state === null ? city : `${city}, ${state}`

  const loadId = outcome?.loadId ?? null
  const refusals = outcome !== undefined && loadId === null ? outcome.errors : []

  // The contacts in DAT's order: the first is said as the row's, the rest beside it — so a
  // row whose own contact was emptied shows the next one, never "No contact" over it.
  const [firstContact, ...moreContacts] = contactsOf(row)

  return (
    <>
      <tr className={historyRow(row.isCancelled)}>
        <td>
          {/* A row DAT has cancelled is history and cannot be taken — decision 3 of the
              capture; one already captured is done. */}
          <input
            type="checkbox"
            checked={chosen}
            disabled={disabled || loadId !== null || row.isCancelled || !isReady(row)}
            aria-label={t('loads.paste.takeRow', { number: row.number })}
            onChange={(event) => onChoose(event.target.checked)}
          />
        </td>
        <td>
          {place(row.originCity, row.originState)} → {place(row.destinationCity, row.destinationState)}
          {row.tripMiles !== null && (
            <>
              <br />
              <span className="muted">{t('loads.paste.miles', { formatted: formatNumber(row.tripMiles, language) })}</span>
            </>
          )}
          {/* What DAT printed in this row that the reader could not place: said on the
              row itself, beside the values it did read (TRKC-93). */}
          {row.skipped.length > 0 && (
            <>
              <br />
              <span className="loads__skipped">
                {t('loads.paste.skipped', { values: row.skipped.map((value) => `“${value}”`).join(', ') })}
              </span>
            </>
          )}
          {row.notes !== null && (
            <>
              <br />
              <span className="muted">{t('loads.paste.rowNotes', { notes: row.notes })}</span>
            </>
          )}
          {/* The same broker on the same route, differing in something: one load or two is
              the dispatcher's call, never the reader's. */}
          {row.sameLaneAs !== null && (
            <>
              <br />
              <span className="muted">{t('loads.paste.sameLane', { row: row.sameLaneAs })}</span>
            </>
          )}
        </td>
        <td>
          {row.pickUpFrom === null
            ? t('loads.paste.missing')
            : row.pickUpTo === null
              ? formatDate(row.pickUpFrom, language)
              : `${formatDate(row.pickUpFrom, language)} – ${formatDate(row.pickUpTo, language)}`}
          {row.isCancelled && (
            <>
              <br />
              <span className="badge badge--blocked">{t('loads.paste.cancelled')}</span>
            </>
          )}
          {/* DAT offers this one for booking on its own board. The platform books
              nothing — the badge exists so the reading can be checked against the
              screen it came from (GPT-5.6 Sol on #29). */}
          {row.isBookable && (
            <>
              <br />
              <span className="badge badge--pending">{t('loads.paste.bookable')}</span>
            </>
          )}
          {loadId !== null && (
            <>
              <br />
              <span className="badge badge--active">{t('loads.paste.captured')}</span>
            </>
          )}
        </td>
        <td>
          {/* DAT's code first, because a code naming two types (VR) is not the same
              claim as the base type this platform reads from it. */}
          {row.equipmentCode ?? t('loads.paste.missing')}
          {/* A code with no type the platform carries says so: the row waits on it. */}
          {row.equipment !== null ? (
            <span className="muted">
              {' · '}
              {row.alternativeEquipment === null
                ? t(`carriers.types.${row.equipment}`)
                : t('loads.equipmentOr', {
                    own: t(`carriers.types.${row.equipment}`),
                    alternative: t(`carriers.types.${row.alternativeEquipment}`),
                  })}
            </span>
          ) : (
            row.equipmentCode !== null && <span className="muted"> · {t('loads.paste.noType')}</span>
          )}
          {/* What the code asks of the unit, said beside it (TRKC-100): VA's air ride, FC's
              chains, Z's hazmat. A load asking for one matches only units that have it. */}
          {requirementsOf(row, t).length > 0 && (
            <span className="muted">{' · '}{requirementsOf(row, t).join(' · ')}</span>
          )}
          <br />
          {/* The weight and full-or-partial are named when missing, as a place or a date
              is: each one holds the row back, and an absence nobody sees explains nothing. */}
          <span className="muted">
            {[
              row.weightPounds === null
                ? t('loads.paste.noWeight')
                : t('loads.paste.pounds', { formatted: formatNumber(row.weightPounds, language) }),
              row.lengthFeet === null ? null : t('loads.paste.feet', { feet: row.lengthFeet }),
              row.loadType === null ? t('loads.paste.noLoadType') : t(`availability.loadTypes.${row.loadType}`),
              // A reefer's temperatures are named when missing too: they hold the row back (TRKC-110).
              row.reeferMinFahrenheit !== null && row.reeferMaxFahrenheit !== null
                ? t('loads.paste.temperatures', { min: row.reeferMinFahrenheit, max: row.reeferMaxFahrenheit })
                : row.equipment === 'Reefer'
                  ? t('loads.paste.noTemperature')
                  : null,
            ]
              .filter((part) => part !== null)
              .join(' · ')}
          </span>
        </td>
        <td className="table__numeric">
          {row.rate === null ? <span className="muted">{t('loads.paste.noRate')}</span> : formatMoney(row.rate, 'USD', language)}
        </td>
        <td className="table__numeric">
          {row.ratePerMile === null ? (
            <span className="muted">—</span>
          ) : (
            t('loads.paste.perMile', { formatted: formatMoney(row.ratePerMile, 'USD', language) })
          )}
        </td>
        {/* DAT's two figures about the broker, each in a column of its own so either can be
            sorted on (Sergio's item 9: the credit score is vital). */}
        <td className="table__numeric">
          {row.creditScore === null ? <span className="muted">{t('loads.paste.notScored')}</span> : row.creditScore}
        </td>
        <td className="table__numeric">{row.daysToPay === null ? <span className="muted">—</span> : row.daysToPay}</td>
        <td>
          {row.company ?? t('loads.paste.missing')}
          <br />
          <span className="muted">{firstContact === undefined ? t('loads.paste.noContact') : contactText(firstContact)}</span>
          {/* One load posted by several reps is one row: the others' contacts travel with it. */}
          {moreContacts.length > 0 && (
            <>
              <br />
              <span className="muted">
                {t('loads.paste.otherContacts', { contacts: moreContacts.map(contactText).join(', ') })}
              </span>
            </>
          )}
          {row.mergedRows.length > 0 && (
            <>
              <br />
              <span className="muted">
                {t('loads.paste.mergedRows', { count: row.mergedRows.length, rows: row.mergedRows.join(', ') })}
              </span>
            </>
          )}
        </td>
        <td>
          {loadId !== null ? (
            <Link to={`/loads/${loadId}`}>{t('loads.paste.openLoad')}</Link>
          ) : (
            !isEditing && (
              <button className="button button--quiet" type="button" onClick={onEdit} disabled={disabled}>
                {t('loads.paste.edit')}
              </button>
            )
          )}
        </td>
      </tr>

      {isEditing && (
        <TablePanelRow columns={COLUMN_COUNT}>
          <RowEditor row={row} onCancel={onCancel} onSave={onSave} />
        </TablePanelRow>
      )}

      {/* Every reason the capture gave, under the row it is about — each translated from
          its code, the record it names one click away. */}
      {refusals.length > 0 && (
        <TablePanelRow columns={COLUMN_COUNT} className="loads__refused">
          <div className="alert">
            <p>{t('loads.paste.refusedRow', { number: row.number })}</p>
            <ul>
              {refusals.map((error) => (
                <li key={error.code}>
                  {refusalSentence(error.code, t, error.subject ?? undefined)}
                  {error.subject !== null && (
                    <>
                      {' '}
                      <RecordLink subject={error.subject} />
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </TablePanelRow>
      )}
    </>
  )
}

/**
 * What a row's code asks of the unit, in words (TRKC-100): its features, the gear a deck must
 * carry, and hazmat — what the screen says beside the type.
 */
function requirementsOf(row: DatPasteRow, t: (key: string) => string): string[] {
  return [
    ...row.features.map((feature) => t(`equipment.features.${feature}`)),
    ...row.securement.map((gear) => t(`availability.gear.${gear}`)),
    ...(row.hazmat ? [t('loads.paste.hazmat')] : []),
  ]
}

/**
 * A draft whose types moved keeps only what the new ones allow: a vent leaves with the van,
 * chains with the last open deck — the load form's rule, so the capture is never refused for it.
 */
function withinTypes<
  T extends { equipment: EquipmentType | ''; alternativeEquipment: EquipmentType | ''; features: EquipmentFeature[]; securement: SecurementGear[] },
>(draft: T): T {
  const own = draft.equipment === '' ? null : draft.equipment
  const also = draft.alternativeEquipment === '' ? null : draft.alternativeEquipment
  const allowed = featuresAllowedOn(own, also)

  return {
    ...draft,
    features: draft.features.filter((feature) => allowed.includes(feature)),
    securement: isOpenDeck(own) || isOpenDeck(also) ? draft.securement : [],
  }
}

/**
 * Correcting one row. Everything DAT printed is editable, because everything DAT
 * printed can be wrong or missing — and a box left empty means the row does not say it,
 * which is different from saying zero.
 *
 * <p>
 * What the reader could not place is decided value by value: kept in the load's notes,
 * or left out — after typing it into its box, when it belongs in one. Until each one is
 * decided the row is not ready, however often it is applied (GPT-5.6 Sol on #37).
 * </p>
 */
function RowEditor({
  row,
  onCancel,
  onSave,
}: {
  row: ReviewedRow
  onCancel: () => void
  onSave: (row: ReviewedRow) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(() => ({
    originCity: row.originCity ?? '',
    originState: row.originState ?? '',
    destinationCity: row.destinationCity ?? '',
    destinationState: row.destinationState ?? '',
    pickUpFrom: row.pickUpFrom ?? '',
    pickUpTo: row.pickUpTo ?? '',
    equipment: (row.equipment ?? '') as EquipmentType | '',
    alternativeEquipment: (row.alternativeEquipment ?? '') as EquipmentType | '',
    features: row.features,
    securement: row.securement,
    hazmat: row.hazmat,
    weightPounds: row.weightPounds?.toString() ?? '',
    lengthFeet: row.lengthFeet?.toString() ?? '',
    loadType: row.loadType ?? '',
    reeferMin: row.reeferMinFahrenheit?.toString() ?? '',
    reeferMax: row.reeferMaxFahrenheit?.toString() ?? '',
    rate: row.rate?.toString() ?? '',
    company: row.company ?? '',
    contactEmail: row.contactEmail ?? '',
    contactPhone: row.contactPhone ?? '',
    skipped: row.skipped,
    notes: row.notes ?? '',
  }))

  // Kept as keys, never as sentences, so a language switch re-translates a complaint
  // already on screen.
  const [unreadable, setUnreadable] = useState<Record<string, string>>({})

  const set = <K extends keyof typeof draft>(field: K, value: (typeof draft)[K]) => {
    setDraft((current) => ({ ...current, [field]: value }))
    setUnreadable(({ [field as string]: _cleared, ...rest }) => rest)
  }

  const blankToNull = (value: string) => (value.trim() === '' ? null : value.trim())

  // A skipped value decided: kept in the load's notes, or left out. Either way it stops
  // holding the row back — the only two ways it does.
  function keepInNotes(value: string) {
    setDraft((current) => ({
      ...current,
      skipped: current.skipped.filter((kept) => kept !== value),
      notes: current.notes.trim() === '' ? value : `${current.notes.trim()}; ${value}`,
    }))
    setUnreadable(({ notes: _cleared, ...rest }) => rest)
  }

  function leaveOut(value: string) {
    setDraft((current) => ({ ...current, skipped: current.skipped.filter((kept) => kept !== value) }))
  }

  function save() {
    // "1..5" parses to nothing, and applying it would drop the figure while the row
    // looked corrected. Blank is a value nobody gave; unreadable is a value nobody can
    // use (GPT-5.6 Sol on #28, met again here by Copilot on #29).
    const refused: Record<string, string> = {}

    for (const field of ['weightPounds', 'lengthFeet', 'rate'] as const) {
      const amount = draft[field].trim() === '' ? 0 : parseAmount(draft[field])

      if (amount === null) {
        refused[field] = 'loads.paste.notANumber'
      }
    }

    // DAT prints a trailer's length in whole feet, and the row's type holds an integer.
    const feet = parseAmount(draft.lengthFeet)

    if (feet !== null && !Number.isInteger(feet)) {
      refused.lengthFeet = 'loads.paste.notWholeFeet'
    }

    // A reefer's temperatures: both or neither, whole degrees, lowest first, within what a
    // reefer holds — the load's own rule, mirrored so a row is never ready over a range the
    // capture would refuse. Neither, on a reefer load, leaves the row waiting for them.
    const reeferMin = draft.reeferMin.trim() === '' ? null : parseAmount(draft.reeferMin)
    const reeferMax = draft.reeferMax.trim() === '' ? null : parseAmount(draft.reeferMax)

    if (reeferShown && (draft.reeferMin.trim() !== '' || draft.reeferMax.trim() !== '')) {
      const degrees = (value: number | null) =>
        value !== null && Number.isInteger(value) && value >= LOWEST_FAHRENHEIT && value <= HIGHEST_FAHRENHEIT

      if (!degrees(reeferMin) || !degrees(reeferMax) || (reeferMin as number) > (reeferMax as number)) {
        refused.reeferMin = 'errors.load_reefer_range_invalid'
      }
    }

    // The box stops typing at the load's limit, but a value kept from the row is added
    // by the button, and a row ready over notes the capture would refuse is no row ready.
    if (draft.notes.trim().length > MAX_NOTES_LENGTH) {
      refused.notes = 'loads.paste.notesTooLong'
    }

    if (Object.keys(refused).length > 0) {
      setUnreadable(refused)
      return
    }

    const rate = parseAmount(draft.rate)

    onSave({
      ...row,
      originCity: blankToNull(draft.originCity),
      originState: blankToNull(draft.originState.toUpperCase()),
      destinationCity: blankToNull(draft.destinationCity),
      destinationState: blankToNull(draft.destinationState.toUpperCase()),
      pickUpFrom: blankToNull(draft.pickUpFrom),
      pickUpTo: blankToNull(draft.pickUpTo),
      equipment: draft.equipment === '' ? null : (draft.equipment as EquipmentType),
      alternativeEquipment: draft.alternativeEquipment === '' ? null : (draft.alternativeEquipment as EquipmentType),
      features: draft.features,
      securement: draft.securement,
      hazmat: draft.hazmat,
      weightPounds: parseAmount(draft.weightPounds),
      lengthFeet: parseAmount(draft.lengthFeet),
      loadType: draft.loadType === '' ? null : (draft.loadType as LoadType),
      // Temperatures belong to a reefer: a row corrected onto other types lets them go.
      reeferMinFahrenheit: reeferShown ? reeferMin : null,
      reeferMaxFahrenheit: reeferShown ? reeferMax : null,
      rate,
      // DAT's rate per mile answers DAT's rate. A corrected rate gets its own, over the trip DAT
      // printed — or none without a trip — or the per-mile filter would judge a figure the row
      // no longer offers (Copilot on #58).
      ratePerMile: rate === row.rate ? row.ratePerMile : perMile(rate, row.tripMiles),
      company: blankToNull(draft.company),
      contactEmail: blankToNull(draft.contactEmail),
      contactPhone: blankToNull(draft.contactPhone),
      skipped: draft.skipped,
      notes: blankToNull(draft.notes),
    })
  }

  const id = (field: string) => `row-${row.number}-${field}`
  const complaint = (field: string) => (unreadable[field] === undefined ? undefined : t(unreadable[field]))
  const featuresOffered = featuresAllowedOn(
    draft.equipment === '' ? null : draft.equipment,
    draft.alternativeEquipment === '' ? null : draft.alternativeEquipment,
  )
  const deckOffered = isOpenDeck(draft.equipment || null) || isOpenDeck(draft.alternativeEquipment || null)
  const reeferShown = draft.equipment === 'Reefer' || draft.alternativeEquipment === 'Reefer'

  return (
    <div className="form loads__editor">
      <FormField name={id('originCity')} label={t('loads.paste.originCity')} value={draft.originCity} onChange={(v) => set('originCity', v)} />
      <FormField name={id('originState')} label={t('loads.paste.originState')} value={draft.originState} onChange={(v) => set('originState', v)} maxLength={2} width="narrow" />
      <FormField name={id('destinationCity')} label={t('loads.paste.destinationCity')} value={draft.destinationCity} onChange={(v) => set('destinationCity', v)} />
      <FormField name={id('destinationState')} label={t('loads.paste.destinationState')} value={draft.destinationState} onChange={(v) => set('destinationState', v)} maxLength={2} width="narrow" />

      <div className="form__break" />

      <FormField name={id('pickUpFrom')} label={t('loads.paste.pickUpFrom')} type="date" value={draft.pickUpFrom} onChange={(v) => set('pickUpFrom', v)} />
      <FormField name={id('pickUpTo')} label={t('loads.paste.pickUpTo')} type="date" value={draft.pickUpTo} onChange={(v) => set('pickUpTo', v)} />
      <FormSelect
        name={id('equipment')}
        label={t('loads.paste.equipment')}
        value={draft.equipment}
        onChange={(v) =>
          // The type the broker also takes is another type: made the base, it is no longer an alternative.
          setDraft((current) =>
            withinTypes({
              ...current,
              equipment: v as EquipmentType | '',
              alternativeEquipment: current.alternativeEquipment === v ? '' : current.alternativeEquipment,
            }),
          )
        }
        options={[
          { value: '', label: t('loads.paste.none') },
          ...EQUIPMENT_TYPES.map((type) => ({ value: type, label: t(`carriers.types.${type}`) })),
        ]}
      />
      <FormSelect
        name={id('alternativeEquipment')}
        label={t('loads.paste.alternative')}
        value={draft.alternativeEquipment}
        onChange={(v) =>
          setDraft((current) => withinTypes({ ...current, alternativeEquipment: v as EquipmentType | '' }))
        }
        options={[
          { value: '', label: t('loads.paste.noAlternative') },
          ...EQUIPMENT_TYPES.filter((type) => type !== draft.equipment).map((type) => ({
            value: type,
            label: t(`carriers.types.${type}`),
          })),
        ]}
      />
      <FormSelect
        name={id('loadType')}
        label={t('loads.paste.loadType')}
        value={draft.loadType}
        onChange={(v) => set('loadType', v as LoadType | '')}
        options={[
          { value: '', label: t('loads.paste.none') },
          ...LOAD_TYPES.map((type) => ({ value: type, label: t(`availability.loadTypes.${type}`) })),
        ]}
      />

      <div className="form__break" />

      {/* What the code asks of the unit, correctable like everything DAT printed (TRKC-100):
          only what the row's types can have is offered. */}
      {featuresOffered.length > 0 && (
        <div className="field field--choices" role="group" aria-labelledby={id('features-label')}>
          <span id={id('features-label')}>{t('loads.form.features')}</span>
          {featuresOffered.map((feature) => (
            <FormCheckbox
              key={feature}
              name={id(`feature-${feature}`)}
              label={t(`equipment.features.${feature}`)}
              checked={draft.features.includes(feature)}
              onChange={(checked) =>
                set(
                  'features',
                  checked ? [...draft.features, feature] : draft.features.filter((existing) => existing !== feature),
                )
              }
            />
          ))}
        </div>
      )}
      {deckOffered && (
        <div className="field field--choices" role="group" aria-labelledby={id('securement-label')}>
          <span id={id('securement-label')}>{t('loads.form.securement')}</span>
          {SECUREMENT_GEAR.map((gear) => (
            <FormCheckbox
              key={gear}
              name={id(`securement-${gear}`)}
              label={t(`availability.gear.${gear}`)}
              checked={draft.securement.includes(gear)}
              onChange={(checked) =>
                set(
                  'securement',
                  checked ? [...draft.securement, gear] : draft.securement.filter((existing) => existing !== gear),
                )
              }
            />
          ))}
        </div>
      )}
      <FormCheckbox
        name={id('hazmat')}
        label={t('loads.form.hazmat')}
        checked={draft.hazmat}
        onChange={(checked) => set('hazmat', checked)}
      />

      {/* DAT prints no temperature, and a reefer load states one (TRKC-110): the dispatcher
          types it here — required when the row rides in a reefer, offered when it also takes one. */}
      {reeferShown && (
        <>
          <div className="form__break" />
          <FormField
            name={id('reeferMin')}
            label={t('loads.form.reeferMin')}
            value={draft.reeferMin}
            onChange={(v) => set('reeferMin', keepDecimal(draft.reeferMin, v))}
            inputMode="numeric"
            required={draft.equipment === 'Reefer'}
            width="narrow"
            error={complaint('reeferMin')}
          />
          <FormField
            name={id('reeferMax')}
            label={t('loads.form.reeferMax')}
            value={draft.reeferMax}
            onChange={(v) => {
              set('reeferMax', keepDecimal(draft.reeferMax, v))
              // The pair's one complaint sits on the lowest box; mending either end clears it.
              setUnreadable(({ reeferMin: _cleared, ...rest }) => rest)
            }}
            inputMode="numeric"
            required={draft.equipment === 'Reefer'}
            width="narrow"
          />
        </>
      )}

      <div className="form__break" />

      {/* keepDecimal undresses a number (commas, spaces, a leading $) and otherwise
          refuses the edit: stripping "4O000" to 4000 or "1e2" to 12 would rewrite the
          figure into a different, valid one, silently (GPT-5.6 Sol on #29). */}
      <FormField name={id('weightPounds')} label={t('loads.paste.weight')} value={draft.weightPounds} onChange={(v) => set('weightPounds', keepDecimal(draft.weightPounds, v))} inputMode="decimal" error={complaint('weightPounds')} />
      <FormField name={id('lengthFeet')} label={t('loads.paste.length')} value={draft.lengthFeet} onChange={(v) => set('lengthFeet', keepDecimal(draft.lengthFeet, v))} inputMode="numeric" width="narrow" error={complaint('lengthFeet')} />
      <FormField name={id('rate')} label={t('loads.paste.rateOffered')} value={draft.rate} onChange={(v) => set('rate', keepDecimal(draft.rate, v))} inputMode="decimal" error={complaint('rate')} />

      <div className="form__break" />

      <FormField name={id('company')} label={t('loads.paste.company')} value={draft.company} onChange={(v) => set('company', v)} />
      <FormField name={id('contactEmail')} label={t('loads.paste.email')} type="email" value={draft.contactEmail} onChange={(v) => set('contactEmail', v)} />
      <FormField name={id('contactPhone')} label={t('loads.paste.phone')} value={draft.contactPhone} onChange={(v) => set('contactPhone', v)} />

      <div className="form__break" />

      {draft.skipped.length > 0 && (
        <fieldset className="loads__decide">
          <legend>{t('loads.paste.decideHeading')}</legend>
          <p className="field__hint">{t('loads.paste.decideHint')}</p>
          <ul>
            {draft.skipped.map((value, index) => (
              <li key={`${index}-${value}`}>
                <span className="loads__skipped">“{value}”</span>
                {/* The value is in each button's name: two rows of "Leave out" say
                    nothing to a screen reader about which value goes. */}
                <button
                  className="button button--quiet"
                  type="button"
                  aria-label={t('loads.paste.keepInNotesValue', { value: `“${value}”` })}
                  onClick={() => keepInNotes(value)}
                >
                  {t('loads.paste.keepInNotes')}
                </button>
                <button
                  className="button button--quiet"
                  type="button"
                  aria-label={t('loads.paste.leaveOutValue', { value: `“${value}”` })}
                  onClick={() => leaveOut(value)}
                >
                  {t('loads.paste.leaveOut')}
                </button>
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      <FormField
        name={id('notes')}
        label={t('loads.paste.notes')}
        value={draft.notes}
        onChange={(v) => set('notes', v)}
        multiline
        width="wide"
        maxLength={MAX_NOTES_LENGTH}
        error={complaint('notes')}
      />

      <div className="form__break" />

      <div className="form__actions">
        <button className="button" type="button" onClick={save}>
          {t('loads.paste.apply')}
        </button>
        <button className="button button--quiet" type="button" onClick={onCancel}>
          {t('loads.paste.cancelEdit')}
        </button>
      </div>
    </div>
  )
}
